import { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { apiClient } from '../utils/api';
import { App } from 'antd';

export interface Producto {
  id: number;
  tipo: number;
  descripcion: string;
}

export interface ContratoFormData {
  ncontrato: string;
  tipo: 'C' | 'V' | '';
  producto_id: number;
  fecha: string;
  comprador: number;
  vendedor: number;
  despachante: number;
  corredor: number;
  entregador: number;
  destinatario: number;
  tarafardo: number;
  kilos: number;
  precio: number;
  fecha_contrato: string;
  fecha_cierre: string;
}

const fechaActual = new Date().toISOString().split('T')[0];

export const useContratoForm = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const { message } = App.useApp();
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const initialValuesRef = useRef<any>(null);

  const [formData, setFormData] = useState<ContratoFormData>({
    ncontrato: '',
    tipo: '',
    producto_id: 0,
    fecha: fechaActual,
    comprador: 0,
    vendedor: 0,
    despachante: 0,
    corredor: 0,
    entregador: 0,
    destinatario: 0,
    tarafardo: 0,
    kilos: 0,
    precio: 0,
    fecha_contrato: fechaActual,
    fecha_cierre: fechaActual
  });

  // Cargar productos
  useEffect(() => {
    const cargarProductos = async () => {
      try {
        setLoadingCatalogos(true);
        const token = await getAccessTokenSilently();
        const response = await apiClient.get<Producto[]>(`${process.env.REACT_APP_BALANZA_URL}/productos/`, { token });
        setProductos(response.data);
      } catch (error) {
        console.error('Error al cargar productos:', error);
        message.error('Error al cargar productos');
      } finally {
        setLoadingCatalogos(false);
      }
    };

    if (user) {
      cargarProductos();
    }
  }, [getAccessTokenSilently, user, message]);

  // Detectar cambios sin guardar
  useEffect(() => {
    if (!initialValuesRef.current) {
      initialValuesRef.current = { ...formData };
      return;
    }

    const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialValuesRef.current);
    setHasUnsavedChanges(hasChanges);
  }, [formData]);

  const resetForm = () => {
    const resetData: ContratoFormData = {
      ncontrato: '',
      tipo: '',
      producto_id: 0,
      fecha: fechaActual,
      comprador: 0,
      vendedor: 0,
      despachante: 0,
      corredor: 0,
      entregador: 0,
      destinatario: 0,
      tarafardo: 0,
      kilos: 0,
      precio: 0,
      fecha_contrato: fechaActual,
      fecha_cierre: fechaActual
    };
    setFormData(resetData);
    initialValuesRef.current = { ...resetData };
    setHasUnsavedChanges(false);
  };

  const selectedProducto = productos.find(p => p.id === formData.producto_id);

  return {
    formData,
    setFormData,
    productos,
    loading,
    loadingCatalogos,
    hasUnsavedChanges,
    selectedProducto,
    resetForm
  };
};

